import React, { useRef, useState } from 'react';

import { ValueBox, ValueStepper, ValueToggle } from '@candu-ai/craft/ui';
import { getCssResetStyleMessage, StylesHierarchy } from 'craft/hooks';
import { ToolboxComponentSchema } from 'craft/types';

import { BorderToggle } from './Border';
import { useBorderCss } from './useBorderCss';
import { useDetailedValues } from './useDetailedValues';

interface Props {
  nodeIds: string[];
  property: string;
  toggleLabel: string;
  inputLabel: string;
  toggles: BorderToggle[];
  schema: ToolboxComponentSchema['Border'];
  stylesHierarchy: StylesHierarchy;
  onSetCss?: (property: string) => void;
}

export const DetailedValues = ({
  nodeIds,
  property,
  toggleLabel,
  inputLabel,
  toggles,
  schema,
  stylesHierarchy,
  onSetCss,
}: Props) => {
  const { cssSelectorId } = schema;
  const { css = {}, setCss } = useBorderCss(nodeIds, cssSelectorId);

  const { fallbackValue, fallbackStyle } = stylesHierarchy?.[property] ?? {
    fallbackValue: undefined,
    fallbackStyle: undefined,
  };
  const { displayedValue, values, toggleValue, updateValue } = useDetailedValues(
    css[property],
    (fallbackValue as string) || '0px',
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<number | undefined>(displayedValue);
  const defaultValue = fallbackValue ? parseFloat(fallbackValue as string) : null;

  return (
    <>
      <ValueBox label={toggleLabel} isInGroup>
        {toggles.map(({ tooltip, icon, value }) => (
          <ValueToggle
            key={value}
            toggleGroup={`${property}-toggle`}
            isActive={!!values[value]}
            icon={icon}
            onClick={() => {
              onSetCss?.(property);
              setCss({ [property]: toggleValue(value) });
            }}
            tooltip={tooltip}
            color={property === 'borderRadius' ? 'light' : undefined}
          />
        ))}
      </ValueBox>
      <ValueBox label={inputLabel} isInGroup>
        <ValueStepper
          ref={inputRef}
          step={1}
          name={`${property}-width`}
          placeholder={typeof inputValue === 'undefined' ? undefined : 'unset'}
          valueSuffix="px"
          editable
          format="inline"
          minValue={0}
          onResetTooltip={fallbackStyle && getCssResetStyleMessage(fallbackStyle)}
          onReset={
            typeof inputValue !== 'undefined' &&
            typeof fallbackValue !== 'undefined' &&
            inputValue !== defaultValue
              ? () => {
                  setCss({ [property]: undefined });
                  setInputValue(defaultValue || 0);
                }
              : undefined
          }
          onChange={(newValue?: number) => {
            setInputValue(newValue);
            // only trigger change when there is no value or the value is within the accepted range
            if (typeof newValue === 'undefined' || (newValue >= 0 && newValue <= 100)) {
              onSetCss?.(property);
              setCss({ [property]: updateValue(newValue || 0) });
            }
          }}
          onBlur={() => {
            if (typeof inputValue === 'undefined') {
              // if the value was deleted at the time of blurring, reflect actual value in the input
              setInputValue(displayedValue);
            }
          }}
          value={inputValue}
        />
      </ValueBox>
    </>
  );
};
